import { useQuery, useMutation, useQueryClient } from 'react-query';
import { axiosClient } from '@/services/utilities';
import { useSearchParams } from 'react-router-dom';

export interface RecipientDetail {
  username: string;
  email: string;
  fullname: string;
  status?: 'unsent' | 'sent' | 'fail';
}

export interface EmailBlastItem {
  id: string;
  subject: string;
  content: string;
  recipients: RecipientDetail[];
  status: 'draft' | 'processed' | 'sent' | 'failed';
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  sentAt?: string;
  totalRecipients: number;
  successCount?: number;
  failedCount?: number;
  // Aliases for compatibility with EmailBlast interface
  recipientCount: number;
  updateAt: string;
}

export interface EmailBlastListResponse {
  items: EmailBlastItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UseEmailBlastQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export const useEmailBlastQuery = (params?: UseEmailBlastQueryParams) => {
  const [searchParam] = useSearchParams();
  
  // Get parameters from URL or use defaults
  const page = Number(searchParam.get('page')) || params?.page || 1;
  const limit = Number(searchParam.get('limit')) || params?.limit || 10;
  const status = searchParam.get('status') || params?.status || '';
  const search = searchParam.get('search') || params?.search || '';

  // Merge URL params with passed params
  const queryParams: UseEmailBlastQueryParams = {
    page,
    limit,
    status,
    search,
    ...params,
  };

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<EmailBlastListResponse>({
    queryKey: ['email-blast', queryParams],
    queryFn: async () => {
      // Build query string from parameters
      const searchParams = new URLSearchParams();
      
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });

      const response = await axiosClient.get(
        `/email-blast?${searchParams.toString()}`
      );

      return response.data;
    },
    enabled: true,
    retry: 3,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  // Handle loading state - return empty data when loading
  if (isLoading || !data) {
    return {
      items: [],
      total: 0,
      page: page,
      limit: limit,
      totalPages: 0,
      isLoading,
      isFetching,
      isError,
      error,
      refetch,
    };
  }

  // Map API response to include aliases for compatibility
  const mappedItems = (data.items || []).map(item => ({
    ...item,
    recipientCount: item.totalRecipients,
    updateAt: item.updatedAt || item.createdAt,
  }));

  return {
    items: mappedItems,
    total: data.total || 0,
    page: data.page || page,
    limit: data.limit || limit,
    totalPages: data.totalPages || 0,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

// Request/Response types for create email blast
export interface CreateEmailBlastRequest {
  subject: string;
  content: string;
  recipients: string[];
}

export interface CreateEmailBlastResponse {
  success: boolean;
  data: EmailBlastItem;
  message: string;
}

// Request/Response types for update email blast
export interface UpdateEmailBlastRequest {
  subject?: string;
  content?: string;
  recipients?: string[];
}

export interface UpdateEmailBlastResponse {
  success: boolean;
  data: EmailBlastItem;
  message: string;
}

// Hook for creating email blast
export const useCreateEmailBlast = () => {
  const queryClient = useQueryClient();
  
  return useMutation<CreateEmailBlastResponse, Error, CreateEmailBlastRequest>({
    mutationFn: async (data) => {
      const response = await axiosClient.post('/email-blast', data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch email blast list
      queryClient.invalidateQueries(['email-blast']);
      // Refetch to ensure the latest data is displayed
      queryClient.refetchQueries(['email-blast']);
    },
  });
};

// Hook for updating email blast (only for drafts)
export const useUpdateEmailBlast = () => {
  const queryClient = useQueryClient();
  
  return useMutation<UpdateEmailBlastResponse, Error, { id: string; data: UpdateEmailBlastRequest }>({
    mutationFn: async ({ id, data }) => {
      const response = await axiosClient.put(`/email-blast/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch email blast list
      queryClient.invalidateQueries(['email-blast']);
      // Invalidate and refetch single email blast
      queryClient.invalidateQueries(['email-blast', variables.id]);
      // Also refetch to ensure the latest data is displayed
      queryClient.refetchQueries(['email-blast']);
    },
  });
};

// Hook for getting a single email blast by ID
export const useEmailBlastById = (id: string) => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<EmailBlastItem>({
    queryKey: ['email-blast', id],
    queryFn: async () => {
      const response = await axiosClient.get(`/email-blast/${id}`);
      const emailBlast = response.data.data || response.data; // Handle both wrapped and unwrapped responses
      return {
        ...emailBlast,
        recipientCount: emailBlast.totalRecipients,
        updateAt: emailBlast.updatedAt || emailBlast.createdAt,
      };
    },
    enabled: !!id,
    retry: 3,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  return {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

// Request/Response types for send email blast
export interface SendEmailBlastResponse {
  success: boolean;
  data: {
    success: number;
    failed: number;
    details: any[];
  };
  message: string;
}

// Hook for sending email blast
export const useSendEmailBlast = () => {
  const queryClient = useQueryClient();
  
  return useMutation<SendEmailBlastResponse, Error, string>({
    mutationFn: async (id: string) => {
      const response = await axiosClient.post(`/email-blast/${id}/send`);
      return response.data;
    },
    onSuccess: (data, id) => {
      // Invalidate and refetch email blast list
      queryClient.invalidateQueries(['email-blast']);
      // Invalidate and refetch single email blast
      queryClient.invalidateQueries(['email-blast', id]);
      // Also refetch to ensure the latest data is displayed
      queryClient.refetchQueries(['email-blast']);
    },
  });
};

// Response type for delete email blast
export interface DeleteEmailBlastResponse {
  success: boolean;
  data: null;
  message: string;
}

// Hook for deleting email blast (draft only)
export const useDeleteEmailBlast = () => {
  const queryClient = useQueryClient();
  
  return useMutation<DeleteEmailBlastResponse, Error, string>({
    mutationFn: async (id: string) => {
      const response = await axiosClient.delete(`/email-blast/${id}`);
      return response.data;
    },
    onSuccess: (data, id) => {
      // Invalidate and refetch email blast list
      queryClient.invalidateQueries(['email-blast']);
      // Invalidate single email blast
      queryClient.invalidateQueries(['email-blast', id]);
      // Refetch to ensure the latest data is displayed
      queryClient.refetchQueries(['email-blast']);
    },
  });
};

// Email Quota Status types
export interface EmailQuotaStatus {
  count: number;
  quota: number;
  remaining: number;
  date: string;
}

// Hook for getting email quota status
export const useEmailQuotaStatus = () => {
  return useQuery<EmailQuotaStatus>({
    queryKey: ['email-quota-status'],
    queryFn: async () => {
      const response = await axiosClient.get('/email-blast/quota/status');
      console.log('Full response:', response.data);

      const payload = response.data.data ?? response.data;

      console.log('Extracted data for hook:', payload);
      return payload as EmailQuotaStatus;
    },
    retry: 3,
    retryDelay: 1000,
    staleTime: 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  });
};
